import React, { useState, useMemo } from "react";
import { submitLead } from "../lib/api";
import { toast } from "sonner";
import {
    Home,
    Building2,
    LayoutGrid,
    Wrench,
    ArrowRight,
    ArrowLeft,
    Zap,
    CheckCircle2,
    Loader2,
    Calculator,
} from "lucide-react";

/* ---------- Estimation logic ---------- */
const PROJECT_TYPES = [
    { id: "adu", label: "ADU / Granny Flat", icon: Home, base: 6500, perSqft: 7 },
    { id: "residential", label: "Residential Remodel / Addition", icon: LayoutGrid, base: 5500, perSqft: 5 },
    { id: "new_sfr", label: "New Single-Family Home", icon: Building2, base: 9500, perSqft: 8 },
    { id: "multifamily", label: "Multifamily", icon: Building2, base: 14500, perSqft: 6 },
    { id: "commercial", label: "Commercial / Tenant Improvement", icon: Wrench, base: 12000, perSqft: 6 },
];

const SIZE_OPTIONS = [
    { id: "xs", label: "Under 500 sqft", sqft: 400 },
    { id: "s", label: "500 – 1,000 sqft", sqft: 750 },
    { id: "m", label: "1,000 – 2,000 sqft", sqft: 1500 },
    { id: "l", label: "2,000 – 4,000 sqft", sqft: 3000 },
    { id: "xl", label: "4,000+ sqft", sqft: 5000 },
];

const TIMELINES = [
    { id: "standard", label: "Standard", multiplier: 1.0, note: "Regular submittal queue" },
    { id: "priority", label: "Priority", multiplier: 1.25, note: "Fast-tracked submittal + corrections" },
    { id: "rush", label: "Rush / Expedited", multiplier: 1.55, note: "Same-day submittal when possible" },
];

function calcEstimate({ projectType, sqft, timeline }) {
    const pt = PROJECT_TYPES.find((p) => p.id === projectType);
    const tl = TIMELINES.find((t) => t.id === timeline);
    if (!pt || !tl || !sqft) return null;
    const raw = (pt.base + pt.perSqft * sqft) * tl.multiplier;
    // Show a range ±15%
    const low = Math.round((raw * 0.85) / 100) * 100;
    const high = Math.round((raw * 1.15) / 100) * 100;
    return { low, high };
}

/* ---------- Component ---------- */
export default function Estimator() {
    const [step, setStep] = useState(1);
    const [projectType, setProjectType] = useState("");
    const [sizeId, setSizeId] = useState("");
    const [timeline, setTimeline] = useState("");
    const [contact, setContact] = useState({ name: "", email: "", phone: "", city: "" });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const sqft = SIZE_OPTIONS.find((s) => s.id === sizeId)?.sqft;
    const estimate = useMemo(() => calcEstimate({ projectType, sqft, timeline }), [projectType, sqft, timeline]);

    const projectLabel = PROJECT_TYPES.find((p) => p.id === projectType)?.label;
    const sizeLabel = SIZE_OPTIONS.find((s) => s.id === sizeId)?.label;
    const timelineLabel = TIMELINES.find((t) => t.id === timeline)?.label;

    const canGoNext =
        (step === 1 && projectType) ||
        (step === 2 && sizeId) ||
        (step === 3 && timeline);

    const next = () => setStep((s) => Math.min(s + 1, 4));
    const back = () => setStep((s) => Math.max(s - 1, 1));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!contact.name.trim() || !contact.email.trim() || !contact.phone.trim()) {
            toast.error("Please fill name, email, and phone to receive your detailed quote.");
            return;
        }
        setSubmitting(true);
        try {
            const description = [
                `Estimator result: $${estimate.low.toLocaleString()}–$${estimate.high.toLocaleString()}`,
                `Project: ${projectLabel}`,
                `Size: ${sizeLabel} (~${sqft} sqft)`,
                `Timeline: ${timelineLabel}`,
            ].join(" | ");
            await submitLead({
                name: contact.name,
                phone: contact.phone,
                email: contact.email,
                city: contact.city,
                project_type: projectLabel,
                description,
                source: "estimator",
            });
            toast.success("Estimate sent! We'll follow up with a detailed quote within 1 business day.");
            setSubmitted(true);
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setStep(1);
        setProjectType("");
        setSizeId("");
        setTimeline("");
        setContact({ name: "", email: "", phone: "", city: "" });
        setSubmitted(false);
    };

    return (
        <section className="py-24 bg-[#F8FAFC] border-t border-[#E2E8F0]" data-testid="estimator-section">
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-12 items-start">
                    {/* Left copy */}
                    <div className="lg:sticky lg:top-28">
                        <div className="inline-flex items-center gap-2 uppercase tracking-[0.2em] text-xs font-semibold text-[#C5A059]">
                            <Calculator size={14} /> Instant Estimate
                        </div>
                        <h2 className="mt-4 text-3xl lg:text-4xl font-semibold text-[#0A2540] tracking-tight">
                            Get a permit cost estimate in 30 seconds.
                        </h2>
                        <p className="mt-5 text-[#475569] leading-relaxed">
                            Answer three quick questions and see a ballpark range for your permit and plan fees—then get a detailed written quote from our team.
                        </p>
                        <ul className="mt-8 space-y-3">
                            {[
                                "Based on 2,400+ real projects",
                                "No obligation — free to use",
                                "Detailed quote follows within 1 business day",
                            ].map((f) => (
                                <li key={f} className="flex items-start gap-3 text-sm text-[#0A2540]">
                                    <CheckCircle2 size={16} className="text-[#C5A059] mt-0.5 flex-shrink-0" />
                                    {f}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Wizard card */}
                    <div
                        className="bg-white border border-[#E2E8F0] p-8 md:p-10 shadow-sm"
                        data-testid="estimator-card"
                    >
                        {/* Progress */}
                        <div className="flex items-center justify-between mb-8">
                            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#475569]">
                                Step {Math.min(step, 4)} of 4
                            </div>
                            <div className="flex gap-1.5">
                                {[1, 2, 3, 4].map((n) => (
                                    <div
                                        key={n}
                                        className={`h-1.5 w-8 transition-colors ${n <= step ? "bg-[#C5A059]" : "bg-[#E2E8F0]"}`}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Step 1: project type */}
                        {step === 1 && (
                            <div data-testid="estimator-step-1">
                                <h3 className="text-2xl font-semibold text-[#0A2540]">What are you building?</h3>
                                <p className="mt-2 text-sm text-[#475569]">Pick the option that best matches your project.</p>
                                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {PROJECT_TYPES.map((p) => {
                                        const active = projectType === p.id;
                                        return (
                                            <button
                                                type="button"
                                                key={p.id}
                                                onClick={() => setProjectType(p.id)}
                                                className={`flex items-center gap-3 p-4 text-left border transition-colors ${
                                                    active
                                                        ? "border-[#C5A059] bg-[#C5A059]/5"
                                                        : "border-[#E2E8F0] hover:border-[#0A2540]"
                                                }`}
                                                data-testid={`estimator-project-${p.id}`}
                                            >
                                                <div className={`w-10 h-10 flex items-center justify-center ${active ? "bg-[#C5A059] text-white" : "bg-[#0A2540] text-[#C5A059]"}`}>
                                                    <p.icon size={18} />
                                                </div>
                                                <span className="font-medium text-[#0A2540]">{p.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Step 2: size */}
                        {step === 2 && (
                            <div data-testid="estimator-step-2">
                                <h3 className="text-2xl font-semibold text-[#0A2540]">Approximate project size?</h3>
                                <p className="mt-2 text-sm text-[#475569]">Pick the range closest to your square footage.</p>
                                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {SIZE_OPTIONS.map((s) => {
                                        const active = sizeId === s.id;
                                        return (
                                            <button
                                                type="button"
                                                key={s.id}
                                                onClick={() => setSizeId(s.id)}
                                                className={`p-4 text-left border transition-colors ${
                                                    active
                                                        ? "border-[#C5A059] bg-[#C5A059]/5"
                                                        : "border-[#E2E8F0] hover:border-[#0A2540]"
                                                }`}
                                                data-testid={`estimator-size-${s.id}`}
                                            >
                                                <span className="font-medium text-[#0A2540]">{s.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Step 3: timeline */}
                        {step === 3 && (
                            <div data-testid="estimator-step-3">
                                <h3 className="text-2xl font-semibold text-[#0A2540]">How fast do you need it?</h3>
                                <p className="mt-2 text-sm text-[#475569]">Rush options include expedited submittal fees.</p>
                                <div className="mt-6 space-y-3">
                                    {TIMELINES.map((t) => {
                                        const active = timeline === t.id;
                                        return (
                                            <button
                                                type="button"
                                                key={t.id}
                                                onClick={() => setTimeline(t.id)}
                                                className={`w-full flex items-center justify-between gap-3 p-4 text-left border transition-colors ${
                                                    active
                                                        ? "border-[#C5A059] bg-[#C5A059]/5"
                                                        : "border-[#E2E8F0] hover:border-[#0A2540]"
                                                }`}
                                                data-testid={`estimator-timeline-${t.id}`}
                                            >
                                                <div>
                                                    <div className="font-medium text-[#0A2540] flex items-center gap-2">
                                                        {t.id === "rush" && <Zap size={14} className="text-[#C5A059]" />}
                                                        {t.label}
                                                    </div>
                                                    <div className="text-xs text-[#475569] mt-0.5">{t.note}</div>
                                                </div>
                                                <span className="text-sm font-semibold text-[#C5A059]">
                                                    {t.multiplier === 1 ? "Base" : `+${Math.round((t.multiplier - 1) * 100)}%`}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Step 4: result + lead capture */}
                        {step === 4 && estimate && !submitted && (
                            <div data-testid="estimator-step-4">
                                <div className="bg-[#0A2540] text-white p-6 md:p-8 mb-6">
                                    <div className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C5A059]">
                                        Estimated Fees
                                    </div>
                                    <div className="mt-2 text-4xl md:text-5xl font-bold font-[Outfit]" data-testid="estimator-result">
                                        ${estimate.low.toLocaleString()} – ${estimate.high.toLocaleString()}
                                    </div>
                                    <p className="mt-3 text-sm text-white/70">
                                        Covers permit processing, architectural plans, and coordination for a <span className="text-[#C5A059]">{projectLabel?.toLowerCase()}</span> of {sizeLabel?.toLowerCase()} on a {timelineLabel?.toLowerCase()} timeline.
                                    </p>
                                </div>

                                <h3 className="text-xl font-semibold text-[#0A2540]">
                                    Want the detailed quote?
                                </h3>
                                <p className="mt-1 text-sm text-[#475569]">
                                    Share your info and a specialist will send a line-item quote tailored to your jurisdiction.
                                </p>

                                <form onSubmit={handleSubmit} className="mt-5 space-y-3" data-testid="estimator-lead-form">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <input
                                            type="text"
                                            required
                                            placeholder="Full name *"
                                            className="est-input"
                                            value={contact.name}
                                            onChange={(e) => setContact({ ...contact, name: e.target.value })}
                                            data-testid="estimator-name"
                                        />
                                        <input
                                            type="tel"
                                            required
                                            placeholder="Phone *"
                                            className="est-input"
                                            value={contact.phone}
                                            onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                                            data-testid="estimator-phone"
                                        />
                                        <input
                                            type="email"
                                            required
                                            placeholder="Email *"
                                            className="est-input"
                                            value={contact.email}
                                            onChange={(e) => setContact({ ...contact, email: e.target.value })}
                                            data-testid="estimator-email"
                                        />
                                        <input
                                            type="text"
                                            placeholder="City, State"
                                            className="est-input"
                                            value={contact.city}
                                            onChange={(e) => setContact({ ...contact, city: e.target.value })}
                                            data-testid="estimator-city"
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="btn-gold w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                                        data-testid="estimator-submit"
                                    >
                                        {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                                        {submitting ? "Sending..." : "Send me the detailed quote"}
                                    </button>
                                </form>

                                <style>{`
                                    .est-input {
                                        width: 100%;
                                        padding: 0.75rem 1rem;
                                        border: 1px solid #E2E8F0;
                                        background: #fff;
                                        color: #0A2540;
                                        font-family: 'Work Sans', sans-serif;
                                        font-size: 0.95rem;
                                        outline: none;
                                        transition: border-color 200ms ease, box-shadow 200ms ease;
                                    }
                                    .est-input:focus {
                                        border-color: #C5A059;
                                        box-shadow: 0 0 0 3px rgba(197,160,89,0.15);
                                    }
                                    .est-input::placeholder { color: #94A3B8; }
                                `}</style>
                            </div>
                        )}

                        {/* Submitted state */}
                        {submitted && (
                            <div className="py-6 text-center" data-testid="estimator-thanks">
                                <div className="w-14 h-14 mx-auto bg-[#C5A059] text-white flex items-center justify-center">
                                    <CheckCircle2 size={28} />
                                </div>
                                <h3 className="mt-5 text-2xl font-semibold text-[#0A2540]">Estimate on the way</h3>
                                <p className="mt-2 text-[#475569]">
                                    A project specialist will reach out within one business day with a detailed, written quote.
                                </p>
                                <button
                                    onClick={reset}
                                    className="mt-6 text-sm font-semibold text-[#0A2540] hover:text-[#C5A059] inline-flex items-center gap-2"
                                    data-testid="estimator-reset"
                                >
                                    Run another estimate <ArrowRight size={14} />
                                </button>
                            </div>
                        )}

                        {/* Nav buttons */}
                        {!submitted && step < 4 && (
                            <div className="mt-8 flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={back}
                                    disabled={step === 1}
                                    className="inline-flex items-center gap-2 text-sm font-medium text-[#475569] hover:text-[#0A2540] disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid="estimator-back"
                                >
                                    <ArrowLeft size={14} /> Back
                                </button>
                                <button
                                    type="button"
                                    onClick={next}
                                    disabled={!canGoNext}
                                    className="btn-blue inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid="estimator-next"
                                >
                                    {step === 3 ? "See my estimate" : "Continue"} <ArrowRight size={14} />
                                </button>
                            </div>
                        )}
                        {!submitted && step === 4 && (
                            <div className="mt-6">
                                <button
                                    type="button"
                                    onClick={back}
                                    className="inline-flex items-center gap-2 text-sm font-medium text-[#475569] hover:text-[#0A2540]"
                                    data-testid="estimator-back-step4"
                                >
                                    <ArrowLeft size={14} /> Change my answers
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
